<template>
  <v-container
    class="pa-0"
    fluid
  >
    <v-row
      no-gutters
      style="overflow-y: auto;"
    >
      <v-col cols="12">
        <table class="divTable text-caption">
          <tr class="divTableHeading">
            <th class="divTableHead text-left">
              #
            </th>

            <th class="divTableHead text-right">
              {{ $t('Payment.Table.Headers.Amount') }}
            </th>

            <th
              v-safe-html="$t('Payment.Table.Headers.PaymentType')"
              class="divTableHead text-center"
            />

            <template v-if="appHasFiscalDocsEnabled && appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD">
              <th class="divTableHead text-left">
                {{ $t('Payment.Table.Headers.Terminal') }}
              </th>

              <th
                class="divTableHead text-center"
                style="width: 100px;"
              >
                {{ $t('Payment.Table.Headers.TransactionType') }}
              </th>

              <th
                class="divTableHead text-center"
                style="width: 100px;"
              >
                {{ $t('Payment.Table.Headers.TransactionResult') }}
              </th>

              <th
                class="divTableHead text-center"
                style="width: 130px;"
              >
                {{ $t('Order.OrderDetailsDialog.Transaction.Date') }}
              </th>

              <th class="divTableHead text-right" />
            </template>

            <template v-else>
              <th class="divTableHead text-right" />
            </template>
          </tr>

          <template v-for="(transaction, index) in transactionsList">
            <tr
              :key="`transaction-${transaction.Id}-${index}`"
              class="divTableRow"
            >
              <td class="divTableCell text-left text-no-wrap">
                <div
                  style="cursor: pointer;"
                  @click="copyToClipboard(transaction.Id)"
                >
                  {{ transaction.Id }}
                </div>

                <div
                  v-if="transaction.OriginalTransactionId"
                  class="grey--text"
                  style="cursor: pointer;"
                  @click="copyToClipboard(transaction.OriginalTransactionId)"
                >
                  {{ transaction.OriginalTransactionId }}
                </div>
              </td>

              <td class="divTableCell text-right text-no-wrap">
                <span v-if="isNegativeTransaction(transaction)">-</span>{{ transaction.Amount | currency }}
              </td>

              <td class="divTableCell text-center text-no-wrap">
                <template v-if="transaction?.Payment?.CardType && CardNameEnum.has(transaction.Payment.CardType)">
                  <card-brand-svg-icon
                    :name="transaction.Payment.CardType"
                    class="mt-1"
                    width="26"
                  />
                </template>

                <template v-else>
                  <template v-for="(payment, idx) in paymentTypes">
                    <v-tooltip
                      v-if="transaction.PaymentType === payment.code"
                      :key="`payment-transaction-${index}-${idx}`"
                      :color="payment.color2"
                      right
                    >
                      <template #activator="{on}">
                        <img
                          v-if="!!payment.image"
                          :src="`${appConfig.LOCATION_DATA.CdnUrl}assets/payments/${payment.image}`"
                          height="18"
                          width="auto"
                          v-on="on"
                        >
                        <v-icon
                          v-else
                          :color="payment.color1"
                          class="mx-1"
                          size="20"
                          v-on="on"
                        >
                          {{ payment.code === PaymentTypeEnum.CASH ? appCurrency.Icon : payment.icon }}
                        </v-icon>
                      </template>

                      <span>
                        {{
                          transaction.Amount | currency
                        }} {{
                          payment.code === PaymentTypeEnum.CASH ? $t('Order.PaymentMethod.Cash').Title.toLocaleUpperCase($i18n.locale) : payment.code === PaymentTypeEnum.CARD ? $t('Order.PaymentMethod.Card').Title.toLocaleUpperCase($i18n.locale) : payment.name.toLocaleUpperCase($i18n.locale)
                        }}
                      </span>
                    </v-tooltip>
                  </template>
                </template>
              </td>

              <template v-if="appHasFiscalDocsEnabled && appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD">
                <td class="divTableCell text-left text-no-wrap">
                  <template v-if="transaction.Payment">
                    <div style="line-height: 14px;">
                      <div
                        v-if="!!transaction.Payment.TerminalNickname"
                        class="font-weight-bold"
                      >
                        {{ transaction.Payment.TerminalNickname }}
                      </div>

                      <div
                        v-if="!!getTerminal(transaction.Payment.TerminalVendorId, transaction.Payment.TerminalModelId)"
                        class="grey--text"
                      >
                        {{
                          getTerminal(transaction.Payment.TerminalVendorId, transaction.Payment.TerminalModelId).Manufacturer
                        }} - {{ getTerminal(transaction.Payment.TerminalVendorId, transaction.Payment.TerminalModelId).Model }}
                      </div>

                      <div
                        v-if="!!getVendorAcquirerTextById(transaction.Payment.TerminalVendorId, transaction.Payment.TerminalAcquirerCode)"
                        class="grey--text"
                      >
                        {{ getVendorAcquirerTextById(transaction.Payment.TerminalVendorId, transaction.Payment.TerminalAcquirerCode) }}
                      </div>
                    </div>
                  </template>
                </td>

                <td class="divTableCell text-center text-no-wrap">
                  <template v-if="transaction.Payment">
                    <status-chip
                      v-if="getTransactionType(transaction.Payment.UserConfirmedAt ? 'SALE' : transaction.Payment.TransactionLastType)"
                      :avatar-color="`${getTransactionType(transaction.Payment.UserConfirmedAt ? 'SALE' : transaction.Payment.TransactionLastType).Color} darken-2`"
                      :color="getTransactionType(transaction.Payment.UserConfirmedAt ? 'SALE' : transaction.Payment.TransactionLastType).Color"
                      :icon="getTransactionType(transaction.Payment.UserConfirmedAt ? 'SALE' : transaction.Payment.TransactionLastType).Icon"
                      :label="getTransactionType(transaction.Payment.UserConfirmedAt ? 'SALE' : transaction.Payment.TransactionLastType).Name"
                      :min-width="115"
                      dense
                    />
                  </template>
                </td>

                <td class="divTableCell text-center text-no-wrap">
                  <div
                    v-if="transaction.Payment"
                    class="text-no-wrap"
                  >
                    <status-chip
                      v-if="getTransactionStatus(transaction.Payment.UserConfirmedAt ? 'COMPLETED' : transaction.Payment.TransactionLastStatus)"
                      :avatar-color="`${getTransactionStatus(transaction.Payment.UserConfirmedAt ? 'COMPLETED' : transaction.Payment.TransactionLastStatus).Color} darken-2`"
                      :color="getTransactionStatus(transaction.Payment.UserConfirmedAt ? 'COMPLETED' : transaction.Payment.TransactionLastStatus).Color"
                      :icon="getTransactionStatus(transaction.Payment.UserConfirmedAt ? 'COMPLETED' : transaction.Payment.TransactionLastStatus).Icon"
                      :label="getTransactionStatus(transaction.Payment.UserConfirmedAt ? 'COMPLETED' : transaction.Payment.TransactionLastStatus).Name"
                      :tooltip="getTransactionStatus(transaction.Payment.UserConfirmedAt ? 'COMPLETED' : transaction.Payment.TransactionLastStatus).Tooltip"
                      dense
                      icon-only
                    />

                    <status-chip
                      v-if="getTransactionResult(transaction.Payment.UserConfirmedAt ? 'APPROVED_MANUAL' : transaction.Payment.TransactionLastResult)"
                      :avatar-color="`${getTransactionResult(transaction.Payment.UserConfirmedAt ? 'APPROVED_MANUAL' : transaction.Payment.TransactionLastResult).ColorAvatar} darken-2`"
                      :color="getTransactionResult(transaction.Payment.UserConfirmedAt ? 'APPROVED_MANUAL' : transaction.Payment.TransactionLastResult).Color"
                      :icon="getTransactionResult(transaction.Payment.UserConfirmedAt ? 'APPROVED_MANUAL' : transaction.Payment.TransactionLastResult).Icon"
                      :label="getTransactionResult(transaction.Payment.UserConfirmedAt ? 'APPROVED_MANUAL' : transaction.Payment.TransactionLastResult).Name"
                      :min-width="115"
                      :tooltip="getTransactionResultTooltip(transaction.Payment.UserConfirmedAt ? 'APPROVED_MANUAL' : transaction.Payment.TransactionLastResult, {user: transaction.User}, transaction.Payment?.Message || '')"
                      dense
                    />
                  </div>
                </td>

                <td class="divTableCell text-right text-no-wrap">
                  <div class="text-no-wrap d-flex align-center justify-center">
                    <display-date-time
                      :date-time="transaction?.Payment?.UpdatedAt || transaction?.UpdatedAt"
                      class="mr-2"
                    />

                    <user-chip
                      v-if="transaction.User"
                      :color="transaction.UserColor"
                      :full-name="transaction.User"
                      :short-name="transaction.UserShortName"
                    />
                  </div>
                </td>

                <td class="divTableCell text-right text-no-wrap">
                  <v-btn
                    v-if="appHasFiscalDocsEnabled && appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD && PaymentTypeEnum.isEftPosPaymentType(transaction.PaymentType) && !order?.ExternalPlatform"
                    icon
                    x-small
                    @click="getItemDetails(transaction)"
                  >
                    <v-icon>mdi-history</v-icon>
                  </v-btn>
                </td>
              </template>

              <template v-else>
                <th
                  class="divTableHead text-right"
                  style="width: 55%;"
                />
              </template>
            </tr>
          </template>

          <tr
            :class="isDarkMode ? 'grey darken-3' : 'grey lighten-4'"
            class="divTableRow text-caption font-weight-bold"
          >
            <td class="divTableCell text-left text-no-wrap text-subtitle-2">
              {{ $t('Order.OrderDetailsDialog.Transaction.Total') }}
            </td>

            <td class="divTableCell text-right text-no-wrap text-subtitle-2">
              {{ transactionsTotal | currency }}
            </td>

            <td
              class="divTableCell"
              colspan="8"
            />
          </tr>
        </table>
      </v-col>
    </v-row>

    <eft-pos-transaction-details-dialog
      :item="selectedDetailsItem"
      :visible.sync="detailsDialogVisible"
    />
  </v-container>
</template>

<script>
import Auth from '@/mixins/auth'
import AppData from '@/mixins/appdata'
import PaymentType from '@/mixins/orders/paymentType'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum'
import {splitDateTimeStrToObj} from '@/lib/helper/helper'
import capitalize from '../../../filters/string/capitalize'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum'
import TerminalsCommon from '@/mixins/payments/terminalsCommon.js'
import StatusChip from '@/components/common/StatusChip.vue'
import {getContrastColor} from '@/lib/color/index.js'
import CardBrandSvgIcon from '@/components/common/CardBrandSvgIcon.vue'
import CardNameEnum from '@/api/Enums/CardNameEnum.js'
import DisplayDateTime from '@/components/common/DisplayDateTime.vue'
import UserChip from '@/components/common/UserChip.vue'
import EftPosTransactionDetailsDialog from '@/components/payments/EftPosTransactionDetailsDialog.vue'

export default {
  name      : 'OrderPaymentTransactionTable',
  components: {EftPosTransactionDetailsDialog, UserChip, DisplayDateTime, CardBrandSvgIcon, StatusChip},
  directives: {},
  mixins    : [Auth, AppData, PaymentType, TerminalsCommon],
  props     : {
    order: {
      type   : Object,
      default: undefined
    },
    transactions: {
      type   : Array,
      default: () => []
    }
  },
  dataStore: {},
  data() {
    return {
      detailsDialogVisible: false,
      selectedDetailsItem : null
    }
  },
  computed: {
    CardNameEnum() {
      return CardNameEnum
    },
    FiscalDocsProviderTypeEnum() {
      return FiscalDocsProviderTypeEnum
    },
    PaymentTypeEnum() {
      return PaymentTypeEnum
    },

    transactionsList() {
      return this.transactions || []
    },

    transactionsCount() {
      return this.transactionsList?.length || 0
    },

    transactionsTotal() {
      return this.transactionsList
        .filter(transaction => !this.isNegativeTransaction(transaction))
        .reduce((a, b) => a + parseFloat(b.Amount) || 0, 0) || 0
    }
  },
  watch: {},
  beforeCreate() {
  },
  created() {
    this.$bus.$on(window.SocketCommand.Payment.EftPos.Get, this.getItemDetailsResponse)
  },
  beforeMount() {
  },
  mounted() {
  },
  beforeUpdate() {
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Payment.EftPos.Get, this.getItemDetailsResponse)
  },
  destroyed() {
  },
  methods: {
    getContrastColor,
    splitDateTimeStrToObj,
    capitalize,

    isNegativeTransaction(transaction) {
      return (!!transaction?.Payment && transaction?.UserConfirmedAt && (transaction.Payment.TransactionLastResult !== 'APPROVED' || (transaction.Payment.TransactionLastResult === 'APPROVED' && transaction.Payment.TransactionLastType === 'REFUND'))) || transaction.DeletedAt
    },

    getItemDetails(item) {
      if (!item) return
      this.selectedItem = item
      window.callAS(window.SocketCommand.Payment.EftPos.Get, {Id: item.Id})
    },

    getItemDetailsResponse(data) {
      this.selectedDetailsItem = {
        Item: this.selectedItem,
        Data: data
      }

      this.detailsDialogVisible = true
    }
  }
}
</script>

<style scoped>

:deep(.v-speed-dial__list) {
  padding: 0;
}

.divTableCell.no-border {
  border-bottom: 0 !important;
}
</style>
